import { Button } from 'primereact/button';

/**
 * Componente estándar para las acciones de pie de página de formularios o modales.
 * Renderiza de forma alineada a la derecha los botones de confirmación (submit) y cancelación.
 * Maneja automáticamente estados de carga, bloqueo y enlace con formularios externos mediante ID.
 *
 * @param {Object} props - Propiedades del componente
 * 
 * --- ACCIONES Y CALLBACKS ---
 * @param {Function} [props.onCancel] - Función callback al presionar "Cancelar". Recibe `false` como argumento (ej. `(false) => setVisible(false)`).
 * @param {boolean} [props.showCancel=true] - Determina si se renderiza el botón de cancelar (requiere además que `onCancel` esté definido).
 * 
 * --- ESTADOS ---
 * @param {boolean} [props.isSaving=false] - Estado de guardado. Muestra un spinner en el botón principal, cambia su texto a "Guardando..." y bloquea ambas acciones.
 * @param {boolean} [props.disabled=false] - Deshabilita manualmente el botón de confirmación/guardado.
 * 
 * --- TEXTOS E ICONOS ---
 * @param {string} [props.submitLabel="Guardar"] - Texto a mostrar en el botón de confirmación cuando no está en estado de guardado.
 * @param {string} [props.cancelLabel="Cancelar"] - Texto a mostrar en el botón de cancelación.
 * @param {string} [props.submitIcon="pi pi-check"] - Icono de PrimeIcons para el botón de confirmación.
 * @param {string} [props.cancelIcon="pi pi-times"] - Icono de PrimeIcons para el botón de cancelación.
 * 
 * --- CONFIGURACIÓN Y ESTILOS ---
 * @param {string|null} [props.formId=null] - ID del elemento `<form>` HTML asociado (`form="formId"`). Permite ubicar el botón fuera de la etiqueta `<form>`.
 * @param {string} [props.className="pt-3 border-top-1 surface-border"] - Clases CSS extra para personalizar el contenedor principal.
 * 
 * @example
 * // Uso básico dentro o fuera de un formulario:
 * <FormActionButtons  onCancel={() => setVisible(false)} isSaving={loadingSave} disabled={!isValidForm} />
 * 
 * @example
 * // Botón submit fuera del <form> usando formId y personalizando etiquetas:
 * <form id="miFormulario" onSubmit={handleSubmit}> ... </form>
 * 
 * <FormActionButtons  formId="miFormulario" submitLabel="Actualizar Registro" submitIcon="pi pi-sync" onCancel={() => navigate(-1)} isSaving={inProgress} />
 */
export const FormActionButtons = ({ onCancel, isSaving = false, formId = null, disabled = false, submitLabel = "Guardar", cancelLabel = "Cancelar", submitIcon = "pi pi-check", cancelIcon = "pi pi-times", className = "pt-3 border-top-1 surface-border", showCancel = true }) => {
    return (
        <div className={`flex justify-content-end align-items-center gap-2 ${className}`}>
            {showCancel && onCancel && (
                <Button type="button" label={cancelLabel} icon={cancelIcon} className="p-button-outlined p-button-secondary p-button-sm"
                    style={{ minWidth: '105px' }} onClick={() => onCancel(false)} disabled={isSaving} />
            )}
            <Button type="submit" form={formId ?? undefined} label={isSaving ? "Guardando..." : submitLabel} icon={submitIcon} className="p-button-primary p-button-sm shadow-1"
                style={{ minWidth: '115px' }} loading={isSaving} disabled={disabled || isSaving} />
        </div>
    );
};